import type { SEOAudit, SeverityItem } from "@/types/analysis";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertTriangle, XCircle, ChevronDown, ArrowRight } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useState } from "react";

const statusIcon = (status: SeverityItem["status"]) => {
  if (status === "pass") return <CheckCircle2 className="w-4 h-4 text-success" />;
  if (status === "warning") return <AlertTriangle className="w-4 h-4 text-warning" />;
  return <XCircle className="w-4 h-4 text-destructive" />;
};

const severityColor = (s: SeverityItem["severity"]) => {
  const map = { low: "bg-info/20 text-info", medium: "bg-warning/20 text-warning", high: "bg-destructive/20 text-destructive", critical: "bg-destructive text-destructive-foreground" };
  return map[s];
};

const ChecklistSection = ({ title, items, score, onViewActionPlan }: { title: string; items?: SeverityItem[]; score: number; onViewActionPlan?: () => void }) => {
  const [open, setOpen] = useState(true);
  const issueCount = items?.filter((i) => i.status === "warning" || i.status === "fail").length ?? 0;

  return (
    <Card className="border-border bg-card">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-secondary/30 transition-colors">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{title}</CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono">{score}/100</Badge>
                <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-2">
            {issueCount > 0 && onViewActionPlan && (
              <button
                onClick={onViewActionPlan}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-primary/5 border border-primary/10 text-xs text-primary hover:bg-primary/10 transition-colors"
              >
                <span className="font-medium">{issueCount} improvement{issueCount > 1 ? "s" : ""} added to Action Plan</span>
                <span className="flex items-center gap-1">View fixes <ArrowRight className="w-3 h-3" /></span>
              </button>
            )}
            {items?.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/20">
                {statusIcon(item.status)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-foreground">{item.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${severityColor(item.severity)}`}>
                      {item.severity}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{item.explanation}</p>
                  {item.evidence && (
                    <p className="text-xs text-muted-foreground/70 mt-1 font-mono bg-background/50 p-1.5 rounded">{item.evidence}</p>
                  )}
                  {(item.status === "warning" || item.status === "fail") && item.recommendation && item.recommendation !== "N/A" && (
                    <div className="mt-2 space-y-1.5">
                      <p className="text-xs text-foreground/80"><span className="font-medium">Recommendation:</span> {item.recommendation}</p>
                      {onViewActionPlan && (
                        <button
                          onClick={onViewActionPlan}
                          className="inline-flex items-center gap-1 text-[11px] text-primary hover:text-primary/80 transition-colors font-medium"
                        >
                          <ArrowRight className="w-3 h-3" />
                          Added to Action Plan – View fixes
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export const SEOAuditTab = ({ data, onViewActionPlan }: { data: SEOAudit; onViewActionPlan?: () => void }) => (
  <div className="space-y-4 mt-4">
    <ChecklistSection title="On-Page SEO" items={data.onPage.items} score={data.onPage.score} onViewActionPlan={onViewActionPlan} />
    <ChecklistSection title="Technical SEO" items={data.technical.items} score={data.technical.score} onViewActionPlan={onViewActionPlan} />
    <ChecklistSection title="Content Quality" items={data.contentQuality.items} score={data.contentQuality.score} onViewActionPlan={onViewActionPlan} />
  </div>
);
